// PROD-3184 — the server's catalogue match, read in ONE place.
//
// Pure. No React, no network.
//
// WHY THIS EXISTS. Master looks every dictated diagnosis and order up in the catalogue, with the
// same search the app runs for that kind (Scrips.Core #101, Master #337, Video #232). React never
// read the answer (Andrew's dev test, 9 Oct): a diagnosis was re-checked by the model's own
// `icdCode`, and the lab and radiology dialogs opened with the name only.
//
// THE RULE. Only `codeStatus === "matched"` with a non-empty `matchedCode` is a match. Everything
// else is "the name is all there is", and keeps today's name-only behaviour:
//   - "ambiguous" (two or more rows), "unmatched" (none), "unchecked" (lookup failed or timed out);
//   - "" (no lookup was made: an older record, an empty name);
//   - ABSENT (an older server, or the AOAI fallback, which leaves the new fields empty).
//
// A match is still a SUGGESTION. It lets the card show the code and lets Accept open the dialog
// with that code chosen. The clinician saves (or does not) in that dialog; nothing is added here.

import type { AmbientCodeMatch } from './ambient-types';

/**
 * `codeStatus` values, exactly as the server sends them.
 *
 * Copied from `Scrips.Core/Shared/Ambient/AmbientWireValues.cs` (`CodeStatus`), Scrips.Core #101 at
 * 6f349b9. `code-status-wire-vocabulary.test.ts` pins this list to that file.
 */
export const AMBIENT_CODE_STATUS = {
  matched: 'matched',
  ambiguous: 'ambiguous',
  unmatched: 'unmatched',
  unchecked: 'unchecked',
  notChecked: '',
} as const;

/** `EncounterProcedure.status` values (`AmbientWireValues.ProcedureStatus`). */
export const PROCEDURE_STATUS = {
  performed: 'performed',
  ordered: 'ordered',
} as const;

/** One catalogue record the server matched. `system` and `display` are absent when the server sent "". */
export interface ServerMatch {
  code: string;
  system?: string;
  display?: string;
}

const trimmed = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/**
 * The record the server matched, or `undefined` when it matched none.
 *
 * Read STRUCTURALLY, so a source of any kind can be asked: a medication, a lab, a diagnosis or a
 * procedure. The status is compared trimmed and lower-cased, as `plan-match` always did; the code is
 * returned as sent (trimmed), never re-cased, because a code is the catalogue's to spell.
 */
export function serverMatchOf(source: AmbientCodeMatch | object | undefined): ServerMatch | undefined {
  if (!source) return undefined;
  const s = source as AmbientCodeMatch;
  if (trimmed(s.codeStatus).toLowerCase() !== AMBIENT_CODE_STATUS.matched) return undefined;
  const code = trimmed(s.matchedCode);
  if (!code) return undefined;
  const system = trimmed(s.matchedSystem);
  const display = trimmed(s.matchedDisplay);
  return { code, ...(system ? { system } : {}), ...(display ? { display } : {}) };
}

/**
 * The code as a card shows it: "CPT 83036", "ICD-10 J02.9", or the code alone when the catalogue
 * has no system (radiology).
 */
export function codeLabel(match: ServerMatch): string {
  return match.system ? `${match.system} ${match.code}` : match.code;
}
